import React, {useEffect, useState} from "react";
import {useSearchParams, useNavigate} from "react-router-dom";
import {Card, Col, Row, Spinner, Container} from "reactstrap";
import {VsiBubble, VsiH4} from "../../../../styled/App.styled";
import ProgressBar from "../../../../components/ProgressBar";
import AtAGlance from "../../../../components/AtAGlance";
import { useVsiAppContext} from "../../../../stores/VsiApp";
import Grid from "../../../../components/Grid";
import {vCenterSummaryDefs} from "../../../../utils/gridColumnDefs/virtual/virtualUnits";
import {getReport} from "../../../../utils/Verbs";
import {useTitleFromComponent} from "../../../../utils/hooks";
import Stockchart from "../../../../components/Stockchart";

const vCenterProgressConfig = [
    {
        type: "single",
        fields: ["avg_cpu_util_pct"],
        thresholds: [
            [80, "#008080"],
            [100, "#d80000"],
        ],
        multiply: true,
        denominator: 100,
        title: "% vCenter CPU Util",
    },
    {
        type: "single",
        fields: ["avg_mem_util_pct"],
        thresholds: [
            [80, "#6495ED"],
            [100, "#d80000"],
        ],
        multiply: true,
        title: "% Memory Util",
        denominator: 200,
    },
    {
        type: "multi",
        key: "vCenter_alerts",
        fields: ["green", "yellow", "red"],
        colors: ["#4CF84C", "#F7FF00", "#F84C4C"],
        title: "vCenter Health Summary",
        denominator: 100,
    },
    {
        type: "single",
        fields: ["avg_storage_util"],
        multiply: true,
        thresholds: [
            [80, "#663399"],
            [100, "#663399"],
        ],
        title: "% Data Storage Util",
        denominator: 100,
    },
];

const vCenterSummaryAag = [
    {
        field: "vms",
        mobile: true,
    },
    {
        field: "clusters",
        mobile: true,
    },
    {
        field: "esx_hosts",
        diff: true,
        mobile: true,
    },
    {
        field: "data_store",
        mobile: true,
    },
    {
        field: "datastore_allocation",
        mobile: true,
    },
    {
        field: "datastore_used",
        mobile: true,
    },
    {
        field: "processors",
        diff: true,
        mobile: true,

    },
    {
        field: "total_memory",
        diff: true,
        mobile: true,

    },
    {
        field: "vmware_version",
        diff: true,
        mobile: true,

    },
    {
        field: "vms_old_hardware",
        diff: true,
        mobile: true,

    },
    {
        field: "alerts",
        diff: true,
        mobile: true,

    },
    {
        field: "red_lights",
        diff: true,
        mobile: true,

    },
    {
        field: "duplicate_vmdks",
        diff: true,
        mobile: true,

    },
    {
        field: "vm_build_capacity",
        diff: true,
        mobile: true,

    }
];

// NOTE: Renamed from vCenter Summary on ticket 3883
export default function DataCenterSummary({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [vCenterGrid, setVCenterGrid] = useState([]);
    const [atAGlanceData, setAtAGlanceData] = useState({});
    const [dashboard, setDashboard] = useState({});
    const [trendData, setTrendData] = useState([]);

    let navigate = useNavigate();

    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const getVCenterSummary = async function () {
        const route = `/api/v6/report/virtual_datacenter_summary_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURIComponent(searchParams.get("vcenter_name"))}&datacenter_name=${encodeURIComponent(searchParams.get("data_center_name"))}`;
        try {
            const token = await getAccessTokenSilently();
            const data = await getReport(route, null, token);
            setVCenterGrid(data?.data?.recordsets[2] || []);
            let summary = data?.data?.recordsets[0][0] || [];
            setDashboard(summary);
            setAtAGlanceData(data?.data?.recordsets[0][0]);
            setTrendData(data?.data?.recordsets[1]);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false)
        }
    };

    useEffect(() => {
        getVCenterSummary();
    }, [periodId]);

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
    }
    const onRowClick = (e) => {
        navigate(`/reports/virtual/cluster/cluster-summary?cluster_name=${encodeURI(e.data.cluster_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&period_id=${e.data.period_id}`)
    }
    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Data Center Summary - {decodeURIComponent(searchParams.get("data_center_name"))}</VsiH4>
            {isLoading ? <Spinner color={"primary"}/> : (
                <>
                    <VsiBubble className={"mb-2"}>
                        <AtAGlance config={vCenterSummaryAag} data={atAGlanceData} size={"lg"}/>
                    </VsiBubble>
                    <VsiBubble className={"mb-2"} autobotName='ProgressBars' autobotInfo={{ data: dashboard, config: vCenterProgressConfig}}>
                        <Container className={"d-flex flex-column justify-content-center mb-3"}>
                            {vCenterProgressConfig.map((blueprints, index) => {
                                const data = blueprints.fields.map((field) => dashboard?.[field] || 0);
                                return (
                                    <ProgressBar
                                        className={"mb-1"}
                                        data={data}
                                        denominator={blueprints.denominator}
                                        title={blueprints.title}
                                        colors={blueprints.colors}
                                        key={`progress-bar-${blueprints.title
                                            .toLowerCase()
                                            .split(" ")
                                            .join("-")}-${index}`}
                                        isMulti={blueprints.type === "multi"}
                                        thresholds={blueprints.thresholds}
                                        multiply={blueprints.multiply}
                                        size={"lg"}
                                    />
                                )
                            })}
                        </Container>
                    </VsiBubble>
                    <Row>
                        <Col xs={6} lg={6}>
                            <VsiBubble autobotName='VMsStockchart'>
                                <Stockchart
                                    series={
                                        [{
                                            name: "# of VMs",
                                            data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['num_vms']])
                                        },
                                            {
                                                name: "VM Build Capacity",
                                                data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['vm_build_capacity']])
                                            },
                                            {
                                                name: "VM Total Capacity",
                                                data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['vm_build_capacity'] + data['num_vms']])
                                            }]
                                    }

                                    config={{
                                        yAxis: [
                                            {
                                                title: {
                                                    text: "# VMs",
                                                }
                                            },
                                        ]
                                    }}/>
                            </VsiBubble>
                        </Col>
                        <Col xs={6} lg={6}>
                            <VsiBubble autobotName='UtilizationStockchart'>
                                <Stockchart series={
                                    [{
                                        name: "CPU Utilization",
                                        data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['pct_cpu_util']])
                                    },
                                        {
                                            name: "Memory Utilization",
                                            data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['pct_memory_util']])
                                        },
                                        {
                                            name: "Disk Utilization",
                                            data: trendData?.map(data => [Date.parse(data['formatted_collection_date']), data['pct_disk_util']])
                                        }]
                                } saveParams={{
                                    vCenterName: searchParams.get("vcenter_name"),
                                    dataCenterName: searchParams.get("data_center_name"),
                                }} config={{
                                    yAxis: [
                                        {
                                            title: {
                                                text: "% Utilization",
                                            }
                                        },
                                    ]
                                }}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                    <VsiBubble>
                        <Grid addStyle data={vCenterGrid}
                              gridText={'Double click to see detailed device reports'}
                              columnDefs={vCenterSummaryDefs}
                              filterChanged={filterChanged}
                            rowDoubleClicked={onRowClick}
                        />
                    </VsiBubble>
                </>
            )}
        </Card>
    )
}
